import axios from "../utils/axios";
import { decryptData, saveData, postErrorSlack } from '../utils/index.jsx';
import * as jose from 'jose';
import { hashPassword, isArgon2Auth, derivePasswordKey, unwrapKek, wrapKek } from '../utils/crypto';

// Imported, not re-declared: routes/urls.js is the canonical home and imports nothing,
// so there is no cycle to justify a private copy. A private copy would drift exactly
// where it hurts most — the URL every expired session is sent to.
import {
    RESEARCHER_LOGIN_ROUTE,
    SIGN_IN_ROUTE as AUTH_SIGN_IN_ROUTE,
    SIGN_UP_ROUTE,
    SIGN_UP_ROUTE_INVESTIGATION,
} from '../routes/urls';
const SESSION_EXPIRED_QUERY_PARAM = 'sessionExpired';
const SESSION_EXPIRED_QUERY_VALUE = '1';
const RETURN_TO_QUERY_PARAM = 'returnTo';
const AUTH_ENDPOINT_PATTERNS = ['/researcher/login'];
const AUTH_ONLY_ROUTES = [RESEARCHER_LOGIN_ROUTE, AUTH_SIGN_IN_ROUTE, SIGN_UP_ROUTE, SIGN_UP_ROUTE_INVESTIGATION];
let sessionExpiryInProgress = false;

function isAuthEndpointRequest(url = '') {
    return AUTH_ENDPOINT_PATTERNS.some((authPattern) => url.includes(authPattern));
}

function isAuthRoute(path = '') {
    const pathWithoutSearchOrHash = path.split('?')[0].split('#')[0];
    return AUTH_ONLY_ROUTES.includes(pathWithoutSearchOrHash);
}

function sanitizeReturnToPath(path) {
    if (typeof path !== 'string' || path.length === 0) {
        return null;
    }
    if (!path.startsWith('/') || path.startsWith('//')) {
        return null;
    }
    if (isAuthRoute(path)) {
        return null;
    }
    return path;
}

function currentPathWithSearchAndHash() {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function clearStoredSession() {
    localStorage.removeItem('jwt');
    if (axios.defaults?.headers) {
        delete axios.defaults.headers.Authorization;
        if (axios.defaults.headers.common) {
            delete axios.defaults.headers.common.Authorization;
        }
    }
}

export function buildSessionExpiredLoginUrl(currentPath) {
    const params = new URLSearchParams();
    params.set(SESSION_EXPIRED_QUERY_PARAM, SESSION_EXPIRED_QUERY_VALUE);

    const returnTo = sanitizeReturnToPath(currentPath);
    if (returnTo) {
        params.set(RETURN_TO_QUERY_PARAM, returnTo);
    }

    return `${RESEARCHER_LOGIN_ROUTE}?${params.toString()}`;
}

export function shouldShowSessionExpiredMessage(search = '') {
    const params = new URLSearchParams(search);
    return params.get(SESSION_EXPIRED_QUERY_PARAM) === SESSION_EXPIRED_QUERY_VALUE;
}

export function resolvePostLoginRoute({ previousRoute, search = '' }) {
    const params = new URLSearchParams(search);
    const routeFromQuery = sanitizeReturnToPath(params.get(RETURN_TO_QUERY_PARAM));
    if (routeFromQuery) {
        return routeFromQuery;
    }

    const routeFromState = sanitizeReturnToPath(previousRoute);
    if (routeFromState) {
        return routeFromState;
    }

    return '/';
}

export function handleSessionExpiredUnauthorized(error, dependencies = {}) {
    const status = error?.response?.status;
    if (status !== 401) {
        return false;
    }

    const requestUrl = error?.config?.url || '';
    if (isAuthEndpointRequest(requestUrl)) {
        return false;
    }

    if (sessionExpiryInProgress) {
        return true;
    }
    sessionExpiryInProgress = true;

    clearStoredSession();

    const getCurrentPath = dependencies.getCurrentPath || currentPathWithSearchAndHash;
    const navigate = dependencies.navigate || ((nextUrl) => window.location.assign(nextUrl));
    const redirectTarget = buildSessionExpiredLoginUrl(getCurrentPath());

    navigate(redirectTarget);
    return true;
}

export function __resetSessionExpiryStateForTests() {
    sessionExpiryInProgress = false;
}

export function fetchUser(){
    return new Promise((resolve, reject) => {
        axios.get(import.meta.env.VITE_APP_API_URL+'/researcher/validate', { headers: {"Authorization" : localStorage.getItem("jwt")}})
            .then((response) => {
                if (response.status === 200) {
                    resolve(true);
                } else {
                    // Handle non-200 status codes (401, 403, etc.)
                    // Since axios.validateStatus treats < 500 as success, we need to handle them here
                    let errorReason = "Unknown error";
                    let errorDetails = {};
                    
                    const status = response.status;
                    if (status === 401) {
                        errorReason = "Unauthorized - Invalid or expired JWT token";
                    } else if (status === 403) {
                        errorReason = "Forbidden - User does not have permission";
                    } else if (status >= 400) {
                        errorReason = `Client Error (${status}) - Bad request`;
                    } else {
                        errorReason = `HTTP Error (${status}) - Unexpected status code`;
                    }
                    errorDetails = {
                        status: status,
                        statusText: response.statusText,
                        data: response.data,
                        type: "http_error"
                    };
                    
                    // Send Slack notification with error details
                    let userEmail = "Unknown";
                    let investigationUuid = localStorage.getItem("uuidInvestigation") || "Not available";
                    
                    if (localStorage.getItem("jwt")) {
                        try {
                            const payload = jose.decodeJwt(localStorage.getItem("jwt"));
                            userEmail = payload.email || payload.uuid || "Unknown";
                        } catch (e) {
                            userEmail = "Unable to decode JWT";
                        }
                    }
                    
                    const slackMessage = `🔴 fetchUser Validation Failed\n\n` +
                        `Reason: ${errorReason}\n` +
                        `User Email: ${userEmail}\n` +
                        `Investigation UUID: ${investigationUuid}\n` +
                        `URL: ${window.location.href}\n` +
                        `Details: ${JSON.stringify(errorDetails)}`;
                    
                    postErrorSlack("auth/fetchUser", slackMessage, errorDetails, null);
                    
                    resolve(false);
                }
            })
            .catch(err => {
                console.log('Catch', err);
                
                // Determine the reason for the failure
                let errorReason = "Unknown error";
                let errorDetails = {};
                
                if (!err.response) {
                    // Check for CORS error first (status 0 or specific error patterns)
                    if (err.code === 0 || 
                        err.response?.status === 0 || 
                        err.message?.toLowerCase().includes('cors') ||
                        err.message?.toLowerCase().includes('cross-origin') ||
                        err.message === "Failed to fetch" ||
                        (err.request && err.request.status === 0)) {
                        errorReason = "CORS Error - Cross-origin request blocked by browser. Server may be missing CORS headers.";
                        errorDetails = {
                            message: err.message,
                            code: err.code,
                            status: err.response?.status || err.request?.status || 0,
                            type: "cors_error"
                        };
                    }
                    // Network error - no response from server
                    else if (err.message === "Network Error" || err.code === "ERR_NETWORK") {
                        errorReason = "Network Error - No internet connection or server unreachable";
                        errorDetails = {
                            message: err.message,
                            code: err.code,
                            type: "network_error"
                        };
                    } else if (err.code === "ECONNREFUSED") {
                        errorReason = "Connection Refused - Server is down or not accepting connections";
                        errorDetails = {
                            message: err.message,
                            code: err.code,
                            type: "network_error"
                        };
                    } else if (err.code === "ETIMEDOUT" || err.message?.includes("timeout")) {
                        errorReason = "Request Timeout - Server took too long to respond";
                        errorDetails = {
                            message: err.message,
                            code: err.code,
                            type: "network_error"
                        };
                    } else {
                        errorReason = `Network Error - ${err.message || err.code || "Unknown network issue"}`;
                        errorDetails = {
                            message: err.message,
                            code: err.code,
                            type: "network_error"
                        };
                    }
                } else {
                    // HTTP error - server responded with error status
                    const status = err.response.status;
                    if (status === 401) {
                        errorReason = "Unauthorized - Invalid or expired JWT token";
                    } else if (status === 403) {
                        errorReason = "Forbidden - User does not have permission";
                    } else if (status >= 500) {
                        errorReason = `Server Error (${status}) - Internal server error`;
                    } else if (status >= 400) {
                        errorReason = `Client Error (${status}) - Bad request`;
                    } else {
                        errorReason = `HTTP Error (${status}) - Unexpected status code`;
                    }
                    errorDetails = {
                        status: status,
                        statusText: err.response.statusText,
                        data: err.response.data,
                        type: "http_error"
                    };
                }
                
                // Send Slack notification with error details
                let userEmail = "Unknown";
                let investigationUuid = localStorage.getItem("uuidInvestigation") || "Not available";
                
                if (localStorage.getItem("jwt")) {
                    try {
                        const payload = jose.decodeJwt(localStorage.getItem("jwt"));
                        userEmail = payload.email || payload.uuid || "Unknown";
                    } catch (e) {
                        userEmail = "Unable to decode JWT";
                    }
                }
                
                const slackMessage = `🔴 fetchUser Validation Failed\n\n` +
                    `Reason: ${errorReason}\n` +
                    `User Email: ${userEmail}\n` +
                    `Investigation UUID: ${investigationUuid}\n` +
                    `URL: ${window.location.href}\n` +
                    `Details: ${JSON.stringify(errorDetails)}`;
                
                postErrorSlack("auth/fetchUser", slackMessage, errorDetails, null);
                
                reject(err);
            }); 
    });
}

export async function signIn(credentials, typeUser) {
  saveData("password", credentials.password);
  const hashed = await hashPassword(credentials.password);
  const postObj = { email: credentials.email, [hashed.field]: hashed.value };

  return new Promise((resolve, reject) => {
    axios.post(import.meta.env.VITE_APP_API_URL+'/'+typeUser+hashed.loginEndpoint, postObj)
        .then(async (response) => {
            if(response.status === 200){
                console.log("TOKEN: "+response.data.jwt);
                saveData("jwt", response.data.jwt);
                saveData("type", typeUser);
                const payload = jose.decodeJwt(localStorage.getItem("jwt"));
                const rawKeyResearcher = decryptData(payload.keyResearcher, localStorage.getItem("password"));
                saveData("rawKeyResearcher", rawKeyResearcher);

                saveData("name", payload.name);
                saveData("surnames", payload.surnames);
                saveData("uuid", payload.uuid);
                axios.defaults.headers['Authorization'] = localStorage.getItem('jwt');

                // Handle v2 KEKs from login/v2 response
                if (response.data.keyDerivationSalt && response.data.keys) {
                    await handleLoginKeks(credentials.password, response.data.keyDerivationSalt, response.data.keys, !!response.data.mustChangePassword);
                }

                // Reset-v2: persist mustChangePassword so the layout/router can force-redirect
                // the user to /change-password until they pick their own password.
                if (response.data.mustChangePassword) {
                    saveData("mustChangePassword", "true");
                } else {
                    localStorage.removeItem("mustChangePassword");
                }

                resolve(response.data);
            }
            else{
                reject(response.data);
            }
        })
        .catch(err => {console.log('Catch', err); reject(err);});
  });
}

/**
 * After login/v2, unwrap KEKs and store them in localStorage.
 * If any KEK has a kekWrappingSalt (provisional), unwrap with DEFAULT_RESEARCH_PASSWORD + salt,
 * then re-wrap with the researcher's own PDK and call provisionKeys.
 */
async function handleLoginKeks(password, keyDerivationSalt, keys, mustChangePassword) {
    try {
        saveData("keyDerivationSalt", keyDerivationSalt);
        const kdfSaltBytes = Uint8Array.from(atob(keyDerivationSalt), c => c.charCodeAt(0));
        const ownPdk = await derivePasswordKey(password, kdfSaltBytes);

        const provisionalKeys = [];
        for (const key of keys) {
            let rawKek;
            if (key.kekWrappingSalt) {
                // Provisional KEK. Two flows produce this:
                //   (a) share-v2: the inviter wrapped with DEFAULT_RESEARCH_PASSWORD + salt.
                //   (b) reset-v2: the admin wrapped with the TEMP PASSWORD the target just
                //       typed to log in (so `password` == temp).
                // We don't know which path produced the row, so try the typed password first
                // (covers reset-v2 and any future flows that use it), fall back to default.
                const provSalt = Uint8Array.from(atob(key.kekWrappingSalt), c => c.charCodeAt(0));
                const candidates = [password, import.meta.env.VITE_APP_DEFAULT_RESEARCH_PASSWORD].filter(Boolean);
                let lastErr;
                rawKek = null;
                for (const candidatePass of candidates) {
                    try {
                        const provPdk = await derivePasswordKey(candidatePass, provSalt);
                        rawKek = await unwrapKek(key.wrappedKey.nonce, key.wrappedKey.ciphertext, provPdk);
                        break;
                    } catch (e) { lastErr = e; }
                }
                if (!rawKek) throw lastErr;

                // Reset-v2: don't re-wrap with the typed (temp) password and don't push to
                // /keys. The target will pick a new permanent password in /change-password
                // and re-wrap there. Just stash the raw KEK so the change-password page can
                // use it.
                if (!mustChangePassword) {
                    const reWrapped = await wrapKek(rawKek, ownPdk);
                    provisionalKeys.push({
                        investigationId: key.investigationId,
                        category: key.category,
                        wrappedKey: reWrapped,
                    });
                }
            } else {
                // Already wrapped with own PDK
                rawKek = await unwrapKek(key.wrappedKey.nonce, key.wrappedKey.ciphertext, ownPdk);
            }

            // Store raw KEK in localStorage as base64
            const kekB64 = btoa(String.fromCharCode(...rawKek));
            saveData(`kek_${key.investigationId}_${key.category}`, kekB64);
        }

        // If there were provisional KEKs (share-v2 path only — skipped for reset-v2),
        // re-wrap and send to server per investigation
        if (provisionalKeys.length > 0) {
            const byInvestigation = {};
            for (const pk of provisionalKeys) {
                if (!byInvestigation[pk.investigationId]) byInvestigation[pk.investigationId] = [];
                byInvestigation[pk.investigationId].push(pk);
            }
            for (const [invId, invKeys] of Object.entries(byInvestigation)) {
                await axios.post(
                    import.meta.env.VITE_APP_API_URL + '/researcher/investigation/' + invId + '/keys',
                    { keys: invKeys },
                    { headers: { Authorization: localStorage.getItem('jwt') } },
                );
            }
        }
    } catch (err) {
        console.error('KEK handling error:', err);
    }
}

export function signUp(credentials) {
  return new Promise((resolve, reject) => {
    axios
      .post("/api/auth/sign-up", credentials)
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data);
        }
        reject(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

export function resetPassword(credentials) {
  return new Promise((resolve, reject) => {
    axios
      .post("/api/auth/reset-password", credentials)
      .then((response) => {
        if (response.status === 200) {
          resolve(response.data);
        }
        reject(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
}
